import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/http';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/relatorios')({ component: RelatoriosPage });

const STATUS_LABEL: Record<string, string> = {
  DONE: 'Concluídas',
  CONFIRMED: 'Confirmadas',
  PENDING: 'Aguardando',
  DISPUTED: 'Em revisão',
  CANCELLED: 'Canceladas',
};
const STATUS_COLOR: Record<string, string> = {
  DONE: 'bg-teal-500',
  CONFIRMED: 'bg-teal-300',
  PENDING: 'bg-amber-300',
  DISPUTED: 'bg-orange-300',
  CANCELLED: 'bg-coral-300',
};
const STATUS_ORDER = ['DONE', 'CONFIRMED', 'PENDING', 'DISPUTED', 'CANCELLED'];

function RelatoriosPage() {
  const { data: a, isLoading } = useQuery({
    queryKey: ['my-analytics'],
    queryFn: () => api.get('therapists/me/analytics').json<any>(),
  });

  if (isLoading || !a) return <p className="text-slate-500 text-sm">Carregando...</p>;

  const total = (Object.values(a.statusMap ?? {}) as number[]).reduce((s, n) => s + n, 0);
  const max = Math.max(1, ...(a.byMonth ?? []).map((m: any) => Math.max(m.total, m.done)));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Relatórios</h1>
        <p className="text-slate-500 text-sm mt-1">Métricas do seu consultório dos últimos 6 meses</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Sessões totais" value={total} />
        <Kpi label="Concluídas" value={a.statusMap?.DONE ?? 0} />
        <Kpi label="Cancelamento" value={`${Number(a.cancellationRate ?? 0).toFixed(1)}%`} tone="coral" />
        <Kpi
          label="Avaliação média"
          value={a.ratingCount > 0 ? `⭐ ${Number(a.ratingAvg).toFixed(1)}` : '—'}
          sub={a.ratingCount > 0 ? `${a.ratingCount} avaliação${a.ratingCount === 1 ? '' : 'ões'}` : undefined}
        />
      </div>

      {/* Gráfico de barras: Sessões por mês */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="font-bold text-slate-800">Sessões por mês</h2>
        <p className="text-sm text-slate-500 mb-5">Total agendado vs efetivamente concluído</p>

        <div className="flex items-center gap-4 mb-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-coral-300" />
            <span className="text-slate-600">Agendadas</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-teal-500" />
            <span className="text-slate-600">Concluídas</span>
          </div>
        </div>

        {(a.byMonth ?? []).length === 0 ? (
          <p className="text-sm text-slate-400 py-8">Sem dados ainda.</p>
        ) : (
          <div className="grid grid-cols-6 gap-3 h-56">
            {(a.byMonth as any[]).map((m) => {
              const totalH = (m.total / max) * 100;
              const doneH = (m.done / max) * 100;
              return (
                <div key={m.month} className="flex flex-col h-full justify-end">
                  <div className="flex items-end justify-center gap-1 h-full">
                    <div className="flex-1 flex items-end max-w-[40px]">
                      <div className="w-full relative">
                        {m.total > 0 && (
                          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-slate-500 font-semibold">{m.total}</span>
                        )}
                        <div className="w-full bg-coral-300 rounded-t-md" style={{ height: `${Math.max(totalH * 2.0, m.total > 0 ? 4 : 0)}px` }} />
                      </div>
                    </div>
                    <div className="flex-1 flex items-end max-w-[40px]">
                      <div className="w-full relative">
                        {m.done > 0 && (
                          <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-slate-500 font-semibold">{m.done}</span>
                        )}
                        <div className="w-full bg-teal-500 rounded-t-md" style={{ height: `${Math.max(doneH * 2.0, m.done > 0 ? 4 : 0)}px` }} />
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 text-center mt-2 capitalize">
                    {String(m.month).split(' ')[0]}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Grid: Retenção + Status */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-bold text-slate-800 mb-4">Retenção de clientes</h2>
          <div className="space-y-3">
            <Row label="Clientes únicos atendidos" value={a.totalClients} />
            <Row label="Clientes recorrentes (≥ 2 sessões)" value={a.returningClients} />
            <Row label="Taxa de retenção" value={`${Number(a.retentionRate ?? 0).toFixed(1)}%`} highlight />
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-2">Distribuição</p>
            <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-teal-500" style={{ width: `${Number(a.retentionRate ?? 0)}%` }} />
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-500 mt-1">
              <span>Recorrentes</span>
              <span>Únicos</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-bold text-slate-800 mb-4">Status das sessões</h2>
          <div className="space-y-3">
            {STATUS_ORDER.map((s) => {
              const count = a.statusMap?.[s] ?? 0;
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={s}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-600">{STATUS_LABEL[s]}</span>
                    <span className="text-slate-500 tabular-nums">
                      {count} ({pct.toFixed(0)}%)
                    </span>
                  </div>
                  <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div className={cn('h-full', STATUS_COLOR[s])} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
            <span className="text-slate-600">Duração média (Meet)</span>
            <span className="font-bold text-slate-800 tabular-nums">
              {Number(a.avgActualDuration ?? 0) > 0 ? `${Number(a.avgActualDuration).toFixed(0)} min` : '—'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string | number; sub?: string; tone?: 'coral' }) {
  const cls = tone === 'coral' ? 'bg-coral-50/50 border-coral-100' : 'bg-white border-slate-200';
  return (
    <div className={cn('rounded-xl border p-5', cls)}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-3xl font-extrabold mt-2 text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={cn('font-bold tabular-nums', highlight ? 'text-2xl text-teal-700' : 'text-slate-700')}>
        {value}
      </span>
    </div>
  );
}
